"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { GameTenKMRRTool } from "@/components/tools/game-tenk-mrr-tool";
import { CompetitionBreakdownChart } from "@/components/charts/competition-breakdown-chart";
import { StartupMrrChart } from "@/components/charts/startup-mrr-chart";
import { NicheHistoryChart } from "@/components/history/niche-history-chart";
import { StartupFilterBar } from "@/components/niches/startup-filter-bar";
import { EmailCaptureForm } from "@/components/retention/email-capture-form";
import { StartupTable } from "@/components/tables/startup-table";
import { StartupDetailDrawer } from "@/components/dashboard/startup-detail-drawer";
import { IdeaValidator } from "@/components/tools/idea-validator";
import { categoryLabel } from "@/lib/analytics";
import { formatDelta, formatMoney, formatPercent, formatPercentile, formatRatioPercent } from "@/lib/format";
import type {
  Entitlements,
  EstimatorBenchmark,
  NicheRanking,
  NicheSnapshot,
  StartupFilters,
  StartupRecord
} from "@/lib/types";

type IdeaValidatorProps = React.ComponentProps<typeof IdeaValidator>;

type Props = {
  category: string;
  ideaValidatorProps?: IdeaValidatorProps;
};

type ApiResponse = {
  source: string;
  category: string;
  niche: NicheRanking | null;
  benchmark: EstimatorBenchmark;
  startups: StartupRecord[];
  history: NicheSnapshot[];
  entitlements: Entitlements;
  hasFullReport?: boolean;
  details?: string;
  error?: string;
};

const DEFAULT_FILTERS: StartupFilters = {
  query: "",
  targetAudience: "all",
  country: "",
  paymentProvider: "",
  onSaleOnly: false,
  growthBand: "all",
  mrrBand: "all"
};

function matchesGrowthBand(startup: StartupRecord, band: StartupFilters["growthBand"]): boolean {
  const growth = startup.growth_30d;
  if (band === "all") return true;
  if (growth === null) return band === "flat";
  if (band === "negative") return growth < 0;
  if (band === "flat") return growth === 0;
  if (band === "positive") return growth > 0;
  return growth >= 25;
}

function matchesMrrBand(startup: StartupRecord, band: StartupFilters["mrrBand"]): boolean {
  if (band === "all") return true;
  if (band === "under1k") return startup.mrr < 1_000;
  if (band === "1kto5k") return startup.mrr >= 1_000 && startup.mrr < 5_000;
  if (band === "5kto10k") return startup.mrr >= 5_000 && startup.mrr < 10_000;
  return startup.mrr >= 10_000;
}

export function NicheDetailShell({ category, ideaValidatorProps }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [niche, setNiche] = useState<NicheRanking | null>(null);
  const [benchmark, setBenchmark] = useState<EstimatorBenchmark | null>(null);
  const [startups, setStartups] = useState<StartupRecord[]>([]);
  const [history, setHistory] = useState<NicheSnapshot[]>([]);
  const [filters, setFilters] = useState<StartupFilters>(DEFAULT_FILTERS);
  const [selectedStartup, setSelectedStartup] = useState<StartupRecord | null>(null);
  const [viewerEmail, setViewerEmail] = useState("");
  const [viewerEmailDraft, setViewerEmailDraft] = useState("");
  const [historyDays, setHistoryDays] = useState(30);
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
  const [hasFullReport, setHasFullReport] = useState(true);

  function applyViewerEmail() {
    setViewerEmail(viewerEmailDraft.trim().toLowerCase());
  }

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const response = await fetch(`/api/niches/${encodeURIComponent(category)}?days=${historyDays}`, {
          cache: "no-store",
          headers: viewerEmail ? { "x-nichemrr-email": viewerEmail } : undefined
        });

        const text = await response.text();
        if (!text) {
          throw new Error(
            response.ok
              ? "Server returned empty response."
              : `Server returned ${response.status}${response.statusText ? ` ${response.statusText}` : ""}.`
          );
        }
        let payload: ApiResponse;
        try {
          payload = JSON.parse(text) as ApiResponse;
        } catch {
          throw new Error(
            response.ok
              ? "Invalid response from server."
              : `Server returned ${response.status}${response.statusText ? ` ${response.statusText}` : ""}.`
          );
        }

        if (!response.ok || !payload.niche) {
          throw new Error(payload.details ?? payload.error ?? "Unable to load niche detail.");
        }

        setNiche(payload.niche);
        setBenchmark(payload.benchmark);
        setStartups(payload.startups ?? []);
        setHistory(payload.history ?? []);
        setEntitlements(payload.entitlements ?? null);
        setHasFullReport(payload.hasFullReport ?? true);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unable to load niche detail.");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [category, historyDays, viewerEmail]);

  const filteredStartups = useMemo(() => {
    return startups.filter((startup) => {
      const query = filters.query.trim().toLowerCase();
      if (query) {
        const haystack = `${startup.name} ${startup.description ?? ""}`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }

      if (filters.targetAudience !== "all" && startup.target_audience !== filters.targetAudience) {
        return false;
      }

      if (filters.country && startup.country !== filters.country) {
        return false;
      }

      if (filters.paymentProvider && startup.payment_provider !== filters.paymentProvider) {
        return false;
      }

      if (filters.onSaleOnly && !startup.on_sale) {
        return false;
      }

      if (!matchesGrowthBand(startup, filters.growthBand)) {
        return false;
      }

      if (!matchesMrrBand(startup, filters.mrrBand)) {
        return false;
      }

      return true;
    });
  }, [filters, startups]);

  const countries = useMemo(
    () => [...new Set(startups.map((startup) => startup.country).filter((value): value is string => Boolean(value)))].sort(),
    [startups]
  );
  const paymentProviders = useMemo(
    () => [...new Set(startups.map((startup) => startup.payment_provider).filter((value): value is string => Boolean(value)))].sort(),
    [startups]
  );

  const trendSummary = useMemo(() => {
    if (history.length < 2) return null;
    const first = history[0];
    const last = history[history.length - 1];
    return {
      scoreDelta: Number((last.opportunityScore - first.opportunityScore).toFixed(1)),
      medianMrrDelta: Math.round(last.medianMrr - first.medianMrr),
      startupCountDelta: last.startupCount - first.startupCount
    };
  }, [history]);

  function exportCsv() {
    const params = new URLSearchParams();
    if (viewerEmail) params.set("email", viewerEmail);
    window.location.href = `/api/export/niches/${category}/startups.csv?${params.toString()}`;
  }

  if (loading && !niche) {
    return (
      <main className="container">
        <div className="loading">
          <div className="spinner" />
          <span className="loading-text">Analyzing {categoryLabel(category)}...</span>
        </div>
      </main>
    );
  }

  if (error || !niche || !benchmark) {
    return (
      <main className="container">
        <section className="hero">
          <h1>{categoryLabel(category)}</h1>
          <p className="hero-sub">This niche could not be loaded right now.</p>
        </section>
        <div className="error-card">{error ?? "Unknown error"}</div>
        <div className="preview-cta">
          <Link href="/dashboard/opportunities" className="btn btn--primary">
            Back to leaderboard
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="container">
      <section className="hero hero--left">
        <p className="section-label">{hasFullReport ? "Niche detail" : "Startup leaderboard"}</p>
        <h1>{niche.label}</h1>
        <p className="hero-sub">
          {hasFullReport
            ? `Opportunity score ${niche.opportunityScore} | median MRR ${formatMoney(niche.medianMrr)} | total MRR ${formatMoney(niche.totalMrr)}.`
            : `${niche.startupCount} tracked startups | median MRR ${formatMoney(niche.medianMrr)} | total MRR ${formatMoney(niche.totalMrr)}.`}
        </p>
        <p className="hero-meta">
          <Link href="/dashboard/opportunities" className="inline-link">
            Back to leaderboard
          </Link>
        </p>
      </section>

      <section className="card market-snapshot">
        <p className="section-label">Market snapshot</p>
        <p className="card-desc">Instant understanding of this niche</p>
        <div className="market-snapshot-grid">
          <div className="market-snapshot-item">
            <p className="market-snapshot-label">Market size</p>
            <p className="market-snapshot-value">{formatMoney(niche.totalMrr)}</p>
            <p className="market-snapshot-meta">Total MRR</p>
          </div>
          <div className="market-snapshot-item">
            <p className="market-snapshot-label">Median MRR</p>
            <p className="market-snapshot-value">{formatMoney(niche.medianMrr)}</p>
            <p className="market-snapshot-meta">Typical startup revenue</p>
          </div>
          <div className="market-snapshot-item">
            <p className="market-snapshot-label">Startups count</p>
            <p className="market-snapshot-value">{niche.trackedStartupCount ?? niche.startupCount}</p>
            <p className="market-snapshot-meta">Tracked in this niche</p>
          </div>
          <div className="market-snapshot-item">
            <p className="market-snapshot-label">Market growth (30d)</p>
            <p className="market-snapshot-value">
              <span className={niche.averageGrowth30d != null && niche.averageGrowth30d > 0 ? "growth-positive" : niche.averageGrowth30d != null && niche.averageGrowth30d < 0 ? "growth-negative" : "growth-neutral"}>
                {formatPercent(niche.averageGrowth30d)}
              </span>
            </p>
            <p className="market-snapshot-meta">Average revenue growth</p>
          </div>
          <div className="market-snapshot-item">
            <p className="market-snapshot-label">Demand score</p>
            <p className="market-snapshot-value">
              {niche.marketDemand > 0 ? niche.marketDemand.toLocaleString() : "—"}
            </p>
            <p className="market-snapshot-meta">Visitors + search impressions (30d)</p>
          </div>
          <div className="market-snapshot-item">
            <p className="market-snapshot-label">Saturation score</p>
            <p className="market-snapshot-value">
              {niche.marketSaturation != null
                ? niche.marketSaturation < 0.01
                  ? niche.marketSaturation.toFixed(4)
                  : niche.marketSaturation.toFixed(2)
                : "—"}
            </p>
            <p className="market-snapshot-meta">
              {niche.marketSaturation != null
                ? niche.marketSaturation < 0.05
                  ? "Low — opportunity"
                  : niche.marketSaturation > 0.2
                    ? "High — crowded"
                    : "Moderate"
                : "No demand data"}
            </p>
          </div>
        </div>
      </section>

      <section className="viewer-tools">
        <div className="card">
          <p className="section-label">Viewer tools</p>
          <div className="viewer-tools-row">
            {hasFullReport && (
              <>
                <input
                  type="email"
                  className="form-input"
                  placeholder="Enter your email to personalize your workspace"
                  value={viewerEmailDraft}
                  onChange={(event) => setViewerEmailDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      applyViewerEmail();
                    }
                  }}
                />
                <button type="button" className="btn btn--ghost" onClick={applyViewerEmail}>
                  Apply
                </button>
                <select
                  className="filter-select"
                  value={String(historyDays)}
                  onChange={(event) => setHistoryDays(Number(event.target.value))}
                >
                  <option value="7">7 days</option>
                  <option value="30">30 days</option>
                  <option value="90">90 days</option>
                  <option value="180">180 days</option>
                </select>
              </>
            )}
            <button
              type="button"
              className="btn btn--ghost"
              onClick={exportCsv}
            >
              Export startups CSV
            </button>
            {hasFullReport && (
              <Link href="/dashboard/metrics" className="btn btn--ghost">
                Metrics playground
              </Link>
            )}
          </div>
          {hasFullReport && (
            <p className="card-desc">
              All features are unlocked. You currently have {entitlements?.historyDaysAllowed ?? 3650} days of history enabled.
            </p>
          )}
        </div>
      </section>

      {ideaValidatorProps && (
        <div style={{ marginBottom: "2.5rem" }}>
          <IdeaValidator {...ideaValidatorProps} />
        </div>
      )}

      {hasFullReport && (
      <section className="stats">
        <div className="stat">
          <p className="stat-label">Opportunity score</p>
          <p className="stat-value stat-value--accent">{niche.opportunityScore}</p>
          <p className="stat-meta">Weighted market attractiveness</p>
        </div>
        <div className="stat">
          <p className="stat-label">7d score delta</p>
          <p className="stat-value stat-value--cyan">{formatDelta(niche.delta?.delta ?? null)}</p>
          <p className="stat-meta">Direction: {niche.delta?.direction ?? "unknown"}</p>
        </div>
        <div className="stat">
          <p className="stat-label">Top 3 concentration</p>
          <p className="stat-value stat-value--success">{formatRatioPercent(niche.top3MrrShare)}</p>
          <p className="stat-meta">Share of MRR held by the top three startups</p>
        </div>
        <div className="stat">
          <p className="stat-label">Founder fit</p>
          <p className="stat-value">{niche.founderFitScore ?? "-"}</p>
          <p className="stat-meta">Solo-founder friendliness score</p>
        </div>
        <div className="stat">
          <p className="stat-label">Tracked startups</p>
          <p className="stat-value">{niche.trackedStartupCount ?? niche.startupCount}</p>
          <p className="stat-meta">Startups included in this niche view</p>
        </div>
        {niche.marketDemand > 0 && (
          <div className="stat">
            <p className="stat-label">Market demand</p>
            <p className="stat-value stat-value--amber">{niche.marketDemand.toLocaleString()}</p>
            <p className="stat-meta">Visitors + search impressions (30d)</p>
          </div>
        )}
        {niche.marketSaturation != null && (
          <div className="stat">
            <p className="stat-label">Market saturation</p>
            <p className="stat-value">{niche.marketSaturation < 0.01 ? niche.marketSaturation.toFixed(4) : niche.marketSaturation.toFixed(2)}</p>
            <p className="stat-meta">
              {niche.marketSaturation < 0.05
                ? "Low — opportunity"
                : niche.marketSaturation > 0.2
                  ? "High — crowded niche"
                  : "Moderate"}
            </p>
          </div>
        )}
        {niche.gapScore != null && niche.gapScore > 0 && (
          <div className="stat">
            <p className="stat-label">Gap score</p>
            <p className="stat-value stat-value--success">{Math.round(niche.gapScore).toLocaleString()}</p>
            <p className="stat-meta">Underserved market signal</p>
          </div>
        )}
      </section>
      )}

      {hasFullReport && trendSummary && (
        <div className="card trend-summary-card">
          <p className="section-label">Trend summary</p>
          <p className="card-desc">
            In the selected window, this niche moved {formatDelta(trendSummary.scoreDelta)} score points, median MRR moved by {formatMoney(trendSummary.medianMrrDelta)}, and startup count changed by {trendSummary.startupCountDelta}.
          </p>
        </div>
      )}

      {benchmark && (() => {
        const empiricalMonths = benchmark.marketMedianMonthsTo10k > 0;
        const heuristicMonths =
          benchmark.marketMedianGrowthPerMonth > 0
            ? Math.round(10_000 / benchmark.marketMedianGrowthPerMonth)
            : null;
        const typicalMonths = empiricalMonths
          ? Math.round(benchmark.marketMedianMonthsTo10k)
          : heuristicMonths;

        return typicalMonths != null && typicalMonths > 0 ? (
          <section className="card time-to-10k-insight">
            <p className="section-label">Time to $10k MRR</p>
            <p className="time-to-10k-value">~{typicalMonths} months</p>
            <p className="card-desc">
              Typical time to $10k MRR: ~{typicalMonths} months.
              {empiricalMonths
                ? " Based on startups in this niche that reached $10k."
                : " Based on median monthly growth in this niche."}
            </p>
          </section>
        ) : null;
      })()}

      {niche.avgRevenuePerVisitor > 0 && (
      <section className="card monetization-insight">
        <p className="section-label">Monetization insight</p>
        <p className="monetization-value">
          {niche.avgRevenuePerVisitor >= 1000
            ? formatMoney(niche.avgRevenuePerVisitor)
            : `$${niche.avgRevenuePerVisitor.toFixed(2)}`}
        </p>
        <p className="card-desc">
          Users in this niche generate{" "}
          {niche.avgRevenuePerVisitor >= 1000
            ? formatMoney(niche.avgRevenuePerVisitor)
            : `$${niche.avgRevenuePerVisitor.toFixed(2)}`}{" "}
          revenue per visitor on average.
        </p>
      </section>
      )}

      {hasFullReport && (
      <div className="detail-grid">
        <section className="card">
          <p className="section-label">Why this niche</p>
          <div className="insight-list">
            {niche.insights.map((insight) => (
              <div key={insight.id} className={`insight-card insight-card--${insight.tone}`}>
                {insight.text}
              </div>
            ))}
          </div>
        </section>

        <section className="card">
          <p className="section-label">Watch this niche</p>
          <p className="card-desc">
            Get notified when {niche.label} changes or lands in the weekly digest.
          </p>
          <EmailCaptureForm
            endpoint="/api/watches/niche"
            payload={{ category: niche.category, watchType: "weekly_digest" }}
            buttonLabel="Weekly digest"
            successMessage={`You are now watching ${niche.label} for the weekly digest.`}
            compact
          />
          <p className="card-desc" style={{ marginTop: 12 }}>
            Or get instant alerts when the opportunity score spikes, new startups enter, or MRR surges.
          </p>
          <EmailCaptureForm
            endpoint="/api/watches/niche"
            payload={{ category: niche.category, watchType: "score_change" }}
            buttonLabel="Get alerts"
            successMessage={`You will receive alerts for ${niche.label}.`}
            compact
          />
        </section>
      </div>
      )}

      {hasFullReport && (
      <div className="detail-grid">
        <section className="card">
          <p className="section-label">Score breakdown</p>
          <div className="score-breakdown-grid">
            <Field label="Market size" value={`${Math.round(niche.scoreBreakdown.totalMrr * 100)} / 100`} />
            <Field label="Growth" value={`${Math.round(niche.scoreBreakdown.averageGrowth30d * 100)} / 100`} />
            <Field label="Demand" value={`${Math.round(niche.scoreBreakdown.marketDemand * 100)} / 100`} />
            <Field label="Monetization" value={`${Math.round(niche.scoreBreakdown.avgRevenuePerVisitor * 100)} / 100`} />
            <Field label="Competition factor" value={niche.scoreBreakdown.competitionFactor.toFixed(2)} />
            <Field label="Sale liquidity" value={`${Math.round(niche.scoreBreakdown.forSaleRatio * 100)} / 100`} />
          </div>
          <p className="card-desc">{niche.scoreExplanation.summary}</p>
        </section>

        <section className="card">
          <p className="section-label">Percentile view</p>
          <div className="pill-row">
            <span className="detail-pill">{formatPercentile(niche.percentiles.opportunityScore)} in score</span>
            <span className="detail-pill">{formatPercentile(niche.percentiles.medianMrr)} in median MRR</span>
            <span className="detail-pill">{formatPercentile(niche.percentiles.totalMrr)} in market size</span>
            <span className="detail-pill">{formatPercentile(niche.percentiles.startupCount)} in crowding</span>
          </div>
        </section>
      </div>
      )}

      {hasFullReport && (
      <div className="detail-grid">
        <section className="card">
          <p className="section-label">Why founders like this niche</p>
          <p className="card-desc">
            {niche.founderFitScore && niche.founderFitScore >= 70
              ? "The blend of revenue, manageable competition, and lower concentration makes this a strong founder candidate."
              : "There is real signal here, but the niche likely needs sharper positioning or stronger distribution."}
          </p>
        </section>
        {niche.founderFitAttributes && (
        <section className="card founder-fit-layer">
          <p className="section-label">Founder fit attributes</p>
          <div className="founder-fit-grid">
            <div className="founder-fit-item">
              <span className="founder-fit-label">Technical complexity</span>
              <span className={`founder-fit-badge founder-fit-badge--${niche.founderFitAttributes.technicalComplexity}`}>
                {niche.founderFitAttributes.technicalComplexity}
              </span>
            </div>
            <div className="founder-fit-item">
              <span className="founder-fit-label">Marketing difficulty</span>
              <span className={`founder-fit-badge founder-fit-badge--${niche.founderFitAttributes.marketingDifficulty}`}>
                {niche.founderFitAttributes.marketingDifficulty}
              </span>
            </div>
            <div className="founder-fit-item">
              <span className="founder-fit-label">Competition intensity</span>
              <span className={`founder-fit-badge founder-fit-badge--${niche.founderFitAttributes.competitionIntensity}`}>
                {niche.founderFitAttributes.competitionIntensity}
              </span>
            </div>
            <div className="founder-fit-item">
              <span className="founder-fit-label">Enterprise vs SMB</span>
              <span className={`founder-fit-badge founder-fit-badge--${niche.founderFitAttributes.enterpriseVsSmb}`}>
                {niche.founderFitAttributes.enterpriseVsSmb}
              </span>
            </div>
          </div>
        </section>
        )}
        <section className="card">
          <p className="section-label">Why this may be hard</p>
          <p className="card-desc">
            {niche.top3MrrShare >= 0.6
              ? "Top-player concentration is high, so entrants may need a narrower wedge."
              : "Competition is present, but market power is not fully locked by a few incumbents."}
          </p>
        </section>
      </div>
      )}

      {hasFullReport && (
      <div className="detail-grid">
        <section className="card">
          <p className="section-label">Market shape</p>
          <div className="score-breakdown-grid">
            <Field label="Median vs average MRR" value={`${formatMoney(niche.medianMrr)} vs ${formatMoney(niche.averageMrr)}`} />
            <Field label="For sale ratio" value={formatRatioPercent(niche.forSaleRatio)} />
            <Field label="Confidence band" value={niche.confidenceBand} />
            <Field label="Top startup" value={niche.topStartup} />
          </div>
        </section>
        <NicheHistoryChart history={history} />
      </div>
      )}

      {(filteredStartups.length > 0 || startups.length > 0) && (
      <section style={{ marginBottom: 24 }}>
        <CompetitionBreakdownChart startups={filteredStartups.length > 0 ? filteredStartups : startups} />
      </section>
      )}

      {hasFullReport && (
      <>
      <p className="section-label">Revenue leaders</p>
      <section style={{ marginBottom: 36 }}>
        <StartupMrrChart startups={filteredStartups.length > 0 ? filteredStartups : startups} />
      </section>
      </>
      )}

      <p className="section-label">Filter startups</p>
      <StartupFilterBar
        filters={filters}
        countries={countries}
        paymentProviders={paymentProviders}
        onChange={setFilters}
      />

      <p className="section-label">Analyzed startups</p>
      <StartupTable
        startups={filteredStartups}
        onSelectStartup={(startup) => setSelectedStartup(startup)}
        selectedStartupSlug={selectedStartup?.slug ?? null}
      />

      {hasFullReport && (
        <>
          <p className="section-label">$10k MRR challenge</p>
          <GameTenKMRRTool
            initialBenchmark={benchmark}
            searchInCard={true}
            title={`${niche.label} $10k MRR challenge`}
            categoryFilter={category}
          />
        </>
      )}

      <StartupDetailDrawer
        startup={selectedStartup}
        niche={niche}
        benchmark={benchmark}
        onClose={() => setSelectedStartup(null)}
      />
    </main>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="drawer-field">
      <span className="drawer-field-label">{label}</span>
      <span className="drawer-field-value">{value}</span>
    </div>
  );
}
